import { useState, useEffect, useContext, useRef } from "react";
import { Row, Col, Container } from "react-bootstrap";
import { useNavigate, Link } from "react-router-dom";
import { Typography, Box, Paper, Button } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import VN from "../components/VN";
import Alerts from "../components/Alerts";
import LoginForm from "../components/login/LoginForm";
import HeroSection from "../components/login/HeroSection";
import { UserDataContext } from "../components/UserDataContext";
import { apiRequest } from "../services/apiService";
import { fetchUserData } from "../services/userService";
import { login, getLoginError } from "../services/authService";

import img4 from "../images/Oldpeople.jpg";
import soon from "../images/soon1.png";

import "../components/LandingPage.css";
import "../components/button.css";
import "@fontsource/lato/300.css";
import "@fontsource/lato/400.css";
import "@fontsource/lato/700.css";

const VERIFICATION_STEPS = [
  "Tożsamość użytkownika",
  "Udokumentowane doświadczenie w opiece",
  "Pozytywne referencje od poprzednich rodzin",
  "Kwalifikacje, dyplomy oraz odbyte kursy medyczne",
  "Indywidualna rozmowa kwalifikacyjno-weryfikacyjna",
];

const Login = () => {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [apiResponse, setApiResponse] = useState({});
  const [showVerification, setShowVerification] = useState(false);
  const [showApiResponse, setShowApiResponse] = useState(false);

  const navigate = useNavigate();
  const { userData, setUserData } = useContext(UserDataContext);
  const communityRef = useRef(null);

  // Auto-scroll do sekcji społeczności po kliknięciu "Zarejestruj się"
  const scrollToCommunity = (e) => {
    e.preventDefault();
    communityRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const clearApiResponse = () => setShowApiResponse(false);

  // Pobieranie IP w trybie produkcyjnym
  useEffect(() => {
    if (process.env.NODE_ENV === "development") return;

    const sendIpToApi = async () => {
      try {
        const ipResponse = await fetch("https://api.ipify.org?format=json");
        if (!ipResponse.ok) throw new Error("Nie udało się pobrać IP");
        const { ip } = await ipResponse.json();
        await apiRequest("send-email-entry", "POST", { ip });
      } catch (error) {
        console.error("Błąd podczas pobierania lub wysyłania IP:", error);
      }
    };

    sendIpToApi();
  }, []);

  // Weryfikacja zalogowanego użytkownika
  useEffect(() => {
    const verifyUser = async () => {
      try {
        await fetchUserData();
      } catch (error) {
        console.error("Błąd podczas weryfikacji użytkownika:", error);
      }
    };
    verifyUser();
  }, []);

  // Obsługa logowania
  const handleSubmit = async (event) => {
    event?.preventDefault();

    // Resetujemy alert przed nową próbą logowania
    setShowApiResponse(false);

    try {
      const response = await login(user, password);
      const error = getLoginError(Number(response.error_msg));

      if (error) {
        setApiResponse(error);
        // Dajemy chwile Reactowi na przetestowanie zmiany stanu `false` -> `true`
        setTimeout(() => setShowApiResponse(true), 50);
        return;
      }

      if (Number(response.error_msg) === 200) {
        setUserData(response);
        const group = Number(response.group);
        const type = Number(response.type);

        if (group === 1) return navigate("/admin");
        if (group === 2) return console.log("Weryfikacja");
        if (group === 3) return navigate("/profile");
        if (type === 2) return navigate("/seeker");
      }
    } catch (error) {
      console.error("Błąd logowania:", error);
      setApiResponse({
        title: "Błąd",
        severity: "error",
        info: "Nie udało się połączyć z serwerem.",
      });
      setTimeout(() => setShowApiResponse(true), 50);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") handleSubmit(event);
  };

  const handleFind = () => {
    if (!userData?.userId) {
      setShowApiResponse(false);
      setApiResponse({
        title: "Błąd",
        severity: "error",
        info: "Musisz być zalogowany, by wyświetlać opiekunki",
      });
      setTimeout(() => setShowApiResponse(true), 50);
    }
  };

  return (
    <div
      style={{
        fontFamily: "'Lato', sans-serif",
        backgroundColor: "#fafafa",
        minHeight: "100vh",
      }}
    >
      <VN />

      {showApiResponse && (
        <Row className="sticky-alerts-container">
          <Alerts data={apiResponse} />
        </Row>
      )}

      <Container style={{ paddingTop: "20px" }}>
        {/* PANEL LOGOWANIA */}
        <LoginForm
          user={user}
          password={password}
          setUser={setUser}
          setPassword={setPassword}
          handleSubmit={handleSubmit}
          handleKeyDown={handleKeyDown}
          clearApiResponse={clearApiResponse}
        />

        {/* LINKI POMOCNICZE */}
        <Row className="text-end mb-3">
          <Col xs={12}>
            Jeśli nie pamiętasz hasła –{" "}
            <Link to="/pass-reset">Reset hasła</Link>
          </Col>
          <Col xs={12}>
            Jeśli nie masz konta –{" "}
            <a
              href="#community"
              onClick={scrollToCommunity}
              style={{
                color: "#0d6efd",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Zarejestruj się
            </a>
          </Col>
        </Row>

        {/* SEKCJA GŁÓWNA */}
        <HeroSection />

        <Row className="align-items-start">
          <Col lg={7} md={12} className="mb-4 mb-lg-0">
            {/* OPIS SERWISU */}
            <Box sx={{ position: "relative", mb: 4 }}>
              <Typography
                variant="h6"
                component="p"
                sx={{
                  color: "#455a64",
                  fontWeight: 400,
                  lineHeight: 1.7,
                  fontSize: "1.2rem",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                Wiemy, jak trudno powierzyć bliską osobę komuś obcemu. <br />
                Dlatego każda opiekunka w naszym serwisie przechodzi{" "}
                <strong>indywidualną, rzetelną weryfikację</strong>. <br />
                Sprawdzamy doświadczenie, dokumenty i referencje – abyś miał
                absolutną pewność, że Twoi bliscy są w najlepszych rękach.
              </Typography>

              <img
                src={soon}
                alt="Wkrótce"
                style={{
                  position: "absolute",
                  top: "-10%",
                  left: "5%",
                  width: "75%",
                  opacity: 0.12,
                  transform: "rotate(-10deg)",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            </Box>

            {/* SEKCJA "DOŁĄCZ DO NASZEJ SPOŁECZNOŚCI" */}
            <Paper
              ref={communityRef}
              elevation={0}
              sx={{
                p: 3,
                mb: 4,
                backgroundColor: "#ffffff",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 8px 25px rgba(0,0,0,0.05)",
              }}
            >
              {!userData?.isLogged && (
                <>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#2c3e50", mb: 2 }}
                  >
                    Dołącz do naszej społeczności
                  </Typography>

                  <Row className="g-3 mb-3">
                    <Col xs={12} sm={6}>
                      <Button
                        component={Link}
                        to="/registerclient"
                        variant="contained"
                        fullWidth
                        size="large"
                        startIcon={<SearchIcon />}
                        sx={{
                          backgroundColor: "#2e7d32",
                          "&:hover": { backgroundColor: "#1b5e20" },
                          fontWeight: 700,
                          textTransform: "none",
                          borderRadius: "10px",
                          py: 1.8,
                          fontSize: "1rem",
                          boxShadow: "0 4px 14px rgba(46, 125, 50, 0.3)",
                        }}
                      >
                        Szukam opiekunki
                      </Button>
                    </Col>

                    <Col xs={12} sm={6}>
                      <Button
                        component={Link}
                        to="/registernanny"
                        variant="contained"
                        fullWidth
                        size="large"
                        startIcon={<PersonAddIcon />}
                        sx={{
                          backgroundColor: "#0288d1",
                          "&:hover": { backgroundColor: "#01579b" },
                          fontWeight: 700,
                          textTransform: "none",
                          borderRadius: "10px",
                          py: 1.8,
                          fontSize: "1rem",
                          boxShadow: "0 4px 14px rgba(2, 136, 209, 0.3)",
                        }}
                      >
                        Dołącz jako opiekunka
                      </Button>
                    </Col>
                  </Row>
                </>
              )}

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", pt: 1 }}>
                <Button
                  variant="text"
                  color="success"
                  onClick={handleFind}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Przeglądaj zweryfikowane profile
                </Button>

                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setShowVerification((prev) => !prev)}
                  startIcon={<VerifiedUserIcon />}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  {showVerification
                    ? "Ukryj proces weryfikacji"
                    : "Sprawdź jak weryfikujemy opiekunki"}
                </Button>
              </Box>
            </Paper>

            {/* OPIS PROCESU WERYFIKACJI */}
            {showVerification && <VerificationSection />}
          </Col>

          {/* OBRAZEK BOŻY */}
          <Col
            lg={5}
            md={12}
            className="d-flex justify-content-center justify-content-lg-end"
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: "16px",
                boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.06)",
                maxWidth: "400px",
                width: "100%",
                transition: "transform 0.3s",
                "&:hover": { transform: "scale(1.02)" },
              }}
            >
              <img
                src={img4}
                alt="Moi Rodzice"
                style={{
                  width: "100%",
                  height: "auto",
                  borderRadius: "10px",
                  display: "block",
                }}
              />
            </Paper>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const VerificationSection = () => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      backgroundColor: "#e8f5e9",
      borderRadius: "16px",
      borderLeft: "5px solid #2e7d32",
      boxShadow: "0px 4px 20px rgba(0,0,0,0.03)",
    }}
  >
    <Typography variant="h6" sx={{ fontWeight: 700, color: "#1b5e20", mb: 2 }}>
      Rzetelna weryfikacja przed dołączeniem do serwisu
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: "#2e7d32", mb: 3, fontWeight: 500 }}
    >
      Każda kandydatka przechodzi restrykcyjny proces sprawdzania przez
      wykwalifikowany personel.
    </Typography>

    <Row className="g-3">
      {VERIFICATION_STEPS.map((text, i) => (
        <Col xs={12} sm={6} key={i} className="d-flex align-items-center">
          <CheckCircleIcon sx={{ color: "#2e7d32", mr: 1.5, fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{ color: "#37474f", fontWeight: 600 }}
          >
            {text}
          </Typography>
        </Col>
      ))}
    </Row>

    <Box sx={{ mt: 3, pt: 2, borderTop: "1px solid rgba(46,125,50,0.1)" }}>
      <Typography
        variant="caption"
        sx={{
          color: "#1b5e20",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        Gwarancja pewności i bezpieczeństwa wyboru
      </Typography>
    </Box>
  </Paper>
);

export default Login;
